<template>
  <Dialog
    :visible="show"
    :modal="true"
    :closable="true"
    :closeButtonProps="{ 'severity': 'primary', 'outlined': true, size: 'small' }"
    :dismissableMask="true"
    :draggable="false"
    :style="computedStyle"
    :breakpoints="computedBreakpoints"
    maximizable
    :maximizeButtonProps="{ 'severity': 'primary', 'outlined': true, size: 'small', 'aria-label': t('Maximizar') }"
    :aria-labelledby="titleId"
    @update:visible="handleVisibleChange"
  >
    <template #header>
      <div class="modal-header-content">
        <h4 :id="titleId" class="modal-title">{{ title }}</h4>
        <Button
          v-if="helpLabel"
          :label="helpLabel"
          icon="pi pi-question-circle"
          severity="primary"
          size="small"
          class="help-button"
          :data-testid="testIdPrefix + 'help-btn'"
          @click="helpFn && helpFn()"
        />
      </div>
    </template>

    <div ref="modalBodyEl" class="modal-body-content">
      <slot></slot>
    </div>

    <template #footer>
      <Button
        :label="closeLabel"
        :icon="closeIcon || 'pi pi-times'"
        severity="secondary"
        outlined
        :data-testid="closeTestId || testIdPrefix + 'close-btn'"
        @click="emit('close')"
      />
      <Button
        v-if="saveLabel"
        :label="saveLabel"
        :icon="saveIcon || 'pi pi-save'"
        severity="primary"
        :data-testid="saveTestId || testIdPrefix + 'save-btn'"
        :disabled="saveDisabledFn && saveDisabledFn()"
        @click="handleSaveClick"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { eventBus } from '../eventBus';

const { t } = useI18n();

type ModalSize = 'small' | 'medium' | 'modalMd' | 'large' | 'xlarge' | 'full' | 'compactDesktop' | 'formOptimized';

// Modal counter service — tracks open modals for body scroll management
let modalCounter = 0;

// Sequence for the per-instance title id (see `titleId` below).
let titleIdSeq = 0;

function incrementModalCount(): number {
  modalCounter++;
  return modalCounter;
}

function decrementModalCount(): number {
  modalCounter = Math.max(0, modalCounter - 1);
  return modalCounter;
}

const MODAL_SIZES: Record<ModalSize, { width: string; breakpoints: Record<string, string> }> = {
  small: { width: '30rem', breakpoints: { '768px': '90vw', '480px': '95vw' } },
  medium: { width: '50rem', breakpoints: { '992px': '85vw', '768px': '90vw', '480px': '95vw' } },
  modalMd: { width: '60rem', breakpoints: { '1200px': '85vw', '992px': '90vw', '768px': '95vw' } },
  large: { width: '70rem', breakpoints: { '1400px': '90vw', '992px': '92vw', '768px': '95vw' } },
  xlarge: { width: '90rem', breakpoints: { '1600px': '92vw', '1200px': '95vw', '992px': '98vw' } },
  full: { width: '98vw', breakpoints: {} },
  compactDesktop: { width: '40rem', breakpoints: { '768px': '95vw', '480px': '98vw' } },
  formOptimized: { width: '35rem', breakpoints: { '768px': '95vw' } },
};

const LEGACY_SIZE_MAP: Record<string, ModalSize> = { 'modal-sm': 'small', 'modal-lg': 'large', '': 'medium' };

const props = withDefaults(defineProps<{
  elemId?: string;
  title?: string;
  saveFn?: () => void;
  saveDisabledFn?: () => boolean;
  closeLabel?: string;
  closeIcon?: string;
  saveLabel?: string;
  saveIcon?: string;
  closeTestId?: string;
  saveTestId?: string;
  size?: ModalSize;
  modalSize?: string;
  modalSizeMobile?: string | null;
  customWidth?: string | null;
  customBreakpoints?: Record<string, string> | null;
  helpFn?: () => void;
  helpLabel?: string;
  show?: boolean;
}>(), {
  saveDisabledFn: () => false,
  closeLabel: 'Fechar',
  closeIcon: 'pi pi-times',
  saveIcon: 'pi pi-save',
  closeTestId: '',
  saveTestId: '',
  size: 'medium',
  modalSize: '',
  modalSizeMobile: null,
  customWidth: null,
  customBreakpoints: null,
  show: false,
});

const emit = defineEmits<{
  close: [];
  dismiss: [];
}>();

// Slot whitelist — `default` (the modal body) is the ONLY public slot.
// The footer is owned by Modal.vue and driven via :close-label /
// :save-label / :save-fn / :save-disabled-fn props. Consumers attempting
// <template #footer> on <modal> are caught by the no-modal-footer-slot
// QA rule at PR time and by the skill doc at design time.
// (Vue's default mode doesn't reject undeclared slots at compile time
// without `strictTemplates: true`, which is a separate cleanup.)
// See: .claude/skills/app-frontend-service-ui-components/SKILL.md ("Modal footer")
defineSlots<{
  default(props: object): unknown;
}>();

const testIdPrefix = computed(() => props.elemId ? `${props.elemId}-` : 'modal-');

// The #header slot REPLACES PrimeVue's own <span :id="ariaLabelledById">, so the
// dialog's aria-labelledby would dangle and the dialog would have NO accessible
// name (axe: aria-dialog-name). Point it at our own .modal-title instead. The id
// must be unique per instance — two modals can be open at once.
const titleId = `modal-title-${++titleIdSeq}`;

const effectiveSize = computed((): ModalSize | null => {
  if (props.customWidth) return null;
  if (props.size !== 'medium' || !props.modalSize) return props.size;
  return LEGACY_SIZE_MAP[props.modalSize] || 'medium';
});

const computedStyle = computed(() => {
  if (props.customWidth) return { width: props.customWidth };
  return { width: MODAL_SIZES[effectiveSize.value as ModalSize].width };
});

const computedBreakpoints = computed(() => {
  if (props.customBreakpoints) return props.customBreakpoints;
  if (props.customWidth) return { '768px': '90vw', '480px': '95vw' };
  return MODAL_SIZES[effectiveSize.value as ModalSize].breakpoints;
});

const originalBodyStyle = ref<string>('');
const originalBodyOverflow = ref<string>('');
const originalScrollY = ref<number>(0);
const modalBodyEl = ref<HTMLElement | null>(null);

// Layer B runtime guard: wraps saveFn invocation and detects "silent" saves
// where the save-fn resolved without producing any observable outcome
// (no *.saved event, no globalMessage.error, no form.validation.failed,
// no .error-message in the modal DOM). Emits modal.save.no-effect so the
// critical-flow tracer can surface the bug instead of waiting 10 s for
// a *.saved event that never fires.
async function handleSaveClick(): Promise<void> {
  if (!props.saveFn) return;

  let sawSaved = false;
  let sawError = false;
  let sawValidationFail = false;

  // Wrap eventBus.emit to intercept any event ending in `.saved` (we can't
  // subscribe to a regex via the existing eventBus API). Direct subscribe
  // for the other two specific events.
  const originalEmit = eventBus.emit.bind(eventBus);
  eventBus.emit = function<T = unknown>(event: string, data?: T): void {
    if (event.endsWith('.saved')) sawSaved = true;
    return originalEmit(event, data);
  };

  const onError = (): void => { sawError = true; };
  const onValidationFail = (): void => { sawValidationFail = true; };
  eventBus.on('globalMessage.error', onError);
  eventBus.on('form.validation.failed', onValidationFail);

  try {
    await props.saveFn?.();
  } finally {
    // 250 ms grace period for async outcomes (network resolves, error toast,
    // validation event) to surface after saveFn resolves.
    await new Promise<void>((resolve) => setTimeout(resolve, 250));

    eventBus.emit = originalEmit;
    eventBus.off('globalMessage.error', onError);
    eventBus.off('form.validation.failed', onValidationFail);

    if (!sawSaved && !sawError && !sawValidationFail) {
      const domHasError = !!modalBodyEl.value?.querySelector('.error-message');
      if (!domHasError) {
        eventBus.emit('modal.save.no-effect', { elemId: props.elemId });
        console.warn(
          `[modal.save.no-effect] elem-id=${props.elemId} — save-fn returned with no observable outcome. Likely silent validateForm() failure in the child form.`,
        );
      }
    }
  }
}

function fixBodyScroll(): void {
  const count = incrementModalCount();
  if (count !== 1) return;

  originalBodyStyle.value = document.body.style.cssText;
  originalBodyOverflow.value = document.body.style.overflow;
  originalScrollY.value = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
  document.body.style.overflow = 'hidden';
}

function restoreBodyScroll(): void {
  const count = decrementModalCount();
  if (count !== 0) return;

  if (originalBodyStyle.value) {
    document.body.style.cssText = originalBodyStyle.value;
  } else {
    document.body.style.overflow = originalBodyOverflow.value || '';
  }
  window.scrollTo(0, originalScrollY.value || 0);
}

function handleVisibleChange(visible: boolean): void {
  if (!visible) {
    if (props.show) {
      emit('dismiss');
    } else {
      // Consumidores que usam v-if + @update:visible (ex.: CaixaModal)
      // em vez de :show — encaminha como close para que o X / mask click
      // do Dialog feche o modal mesmo sem :show.
      emit('close');
    }
  }
}

function handleEscapeKey(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    if (props.show) {
      emit('dismiss');
    } else {
      emit('close');
    }
  }
}

watch(() => props.show, (newVal) => {
  if (newVal) {
    fixBodyScroll();
  } else {
    restoreBodyScroll();
  }
});

onMounted(() => document.addEventListener('keydown', handleEscapeKey));
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscapeKey);
  restoreBodyScroll();
});
</script>

<style scoped>
.modal-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--app-primary-color, #e6b800);
}

.help-button {
  margin-left: auto;
}

.help-button :deep(.p-button) {
  background-color: rgba(255, 255, 255, 0.2) !important;
  border-color: white !important;
  color: white !important;
}

.help-button :deep(.p-button:hover) {
  background-color: rgba(255, 255, 255, 0.3) !important;
}

.modal-body-content {
  background-color: var(--app-bg-medium, #e2e2e2);
  padding: 1rem;
  min-height: 200px;
  overflow-y: auto;
}

:deep(.p-dialog) {
  border: none !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

:deep(.p-dialog-content) {
  padding: 0;
  border: none !important;
}

:deep(.p-dialog-header) {
  border: none !important;
}

:deep(.p-dialog-footer) {
  padding: 1rem 1.5rem;
  display: flex !important;
  justify-content: space-between !important;
  gap: 0.5rem;
  border: none !important;
}

:deep(.p-dialog-footer button),
:deep(.p-dialog-footer .p-button) {
  flex: 1 1 0% !important;
  min-width: 0 !important;
  width: auto !important;
}

:deep(.p-dialog-footer .p-button-label) {
  flex: 1;
  text-align: center;
}

/*
 * Disable PrimeVue enter/leave transitions inside modals.
 * This ensures that form.ready events mean "DOM is stable and clickable".
 * Without this, PrimeVue components (Message, TabPanel, etc.) animate
 * on mount/unmount, causing race conditions where elements are in the DOM
 * but not yet interactive, or get detached mid-click during re-renders.
 * Page-level animations (toasts, sidebars) are unaffected — this is scoped to Dialog content.
 */
:deep([class*="-enter-active"]),
:deep([class*="-leave-active"]) {
  transition: none !important;
  animation: none !important;
}
</style>
